<script setup lang="ts">
import { generateSkuCode } from "core/helpers";
import { FOUR_WORDS_ONLY_REGEXP } from "shared/constants";
import { formatCurrency } from "shared/helpers";
import { capitalize } from "vue";
import * as yup from "yup";

import { getMaterialDefault } from "~/common/defaults/material.default";
import { getSizeDefault } from "~/common/defaults/size.default";
import type { CartItem } from "~/common/interfaces/cart-item.interface";
import type { ShopComponentsResponseDto } from "~/common/interfaces/dto/shop/shop-components-response.dto";
import type { ShopProductResponseDto } from "~/common/interfaces/dto/shop/shop-product-response.dto";
import type { ItemCustomization } from "~/common/interfaces/item-customization.interface";
import type { MaterialTrimmed } from "~/common/interfaces/material-trimmed";
import type { SizeTrimmed } from "~/common/interfaces/size-trimmed";

const { t } = useI18n();

const props = defineProps({
	product: {
		type: Object as PropType<ShopProductResponseDto>,
		required: true
	},
	pricePolicies: {
		type: Array as PropType<ShopComponentsResponseDto["pricePolicies"]>,
		required: true
	}
});
const emit = defineEmits<{(event: "add-item-to-cart", cartItem: CartItem): void}>();

const { handleSubmit, errors, meta, resetField } = useForm<ItemCustomization>({
	initialValues: {
		size: getSizeDefault(),
		material: getMaterialDefault(),
		useCustomText: false,
		text: "",
		amount: 1
	},
	validationSchema: yup.object({
		useCustomText: yup.boolean().required(),
		text: yup.string().when("useCustomText", {
			is: true,
			then: value => value.required(capitalize(t("inputCustomTextRequiredError")))
		}).matches(FOUR_WORDS_ONLY_REGEXP, { message: capitalize(t("inputCustomTextRegExpError")) })
	})
});

const { value: size } = useField<SizeTrimmed>("size");
const { value: material } = useField<MaterialTrimmed>("material");
const { value: useCustomText } = useField<boolean>("useCustomText");
const { value: text } = useField<string>("text");
const { value: amount } = useField<number>("amount");

const currentPricePolicy = computed(() => props.pricePolicies.find(pricePolicy => pricePolicy.kind === props.product.kind.id &&
			pricePolicy.materials.includes(material.value.id) &&
			pricePolicy.sizes.includes(size.value.id)));
const currentItems = computed(() => props.product.items.filter(item => item.material.id === material.value.id && item.size.id === size.value.id));
const currentItem = computed(() => {
	if (currentItems.value.length && !useCustomText.value) {
		return currentItems.value[0];
	} else {
		return { discountedPrice: 0, price: currentPricePolicy.value?.price || 0 };
	}
});
const inStockAmount = computed(() => currentItems.value.reduce((acc, next) => {
	acc += next.ids.length;

	return acc;
}, 0));
const skuCode = computed(() => {
	if (!size.value || !material.value) {
		return "-";
	} else {
		return generateSkuCode(props.product.id, material.value.id, size.value.id);
	}
});

const addToCart = handleSubmit((form: ItemCustomization) => {
	emit("add-item-to-cart", {
		product: props.product,
		pricePolicy: currentPricePolicy.value,
		size: form.size,
		material: form.material,
		text: form.text,
		amount: form.amount
	});

	resetField("useCustomText");
	resetField("text");
	resetField("amount");
});
</script>

<template>
	<div class="flex flex-col gap-y-7">
		<div>
			<h1 class="text-2xl">
				{{ product.name }}
			</h1>
			<div class="mt-2 flex items-end justify-between w-full">
				<div class="flex gap-x-2 text-[22px]">
					<span
						class="font-medium"
						:class="currentItem.discountedPrice && 'line-through font-normal text-green-dark'"
					>
						{{ formatCurrency(currentItem.price) }}
					</span>
					<span v-if="currentItem.discountedPrice" class="font-medium">
						{{ formatCurrency(currentItem.discountedPrice) }}
					</span>
				</div>
				<div class="font-normal text-lg">
					<div v-if="inStockAmount && !useCustomText">
						<b class="font-medium text-[22px]">{{ inStockAmount }}</b> pcs. in-stock
					</div>
				</div>
			</div>
			<p class="text-sm py-5">
				{{ product.description }}
			</p>
		</div>
		<div>
			<ProductDetailsCustomizer
				v-model:size="size"
				v-model:material="material"
				v-model:useCustomText="useCustomText"
				v-model:text="text"
				:product="product"
				:errors="errors"
			/>
			<div class="mt-6 flex justify-between gap-x-4">
				<CommonAmount
					v-model="amount"
					class="flex items-center justify-between border-2 border-gray text-gray-dark text-lg w-1/4 min-w-11"
					:min="1"
					:max="100"
				/>
				<AppButton
					class="w-3/4"
					fill
					:disabled="!meta.valid"
					@click="addToCart"
				>
					{{ $t('addToCart') }}
				</AppButton>
			</div>
		</div>
		<div class="text-xs text-green-dark">
			<div>
				<span class="uppercase">{{ $t('code') }}:</span>
				{{ skuCode }}
			</div>
			<div>
				<span class="uppercase">{{ $t('category') }}:</span>
				{{ product.kind.name }}
			</div>
			<div v-if="product.collections.length">
				<span class="uppercase">{{ $t('collection') }}:</span>
				{{ product.collections.join(', ') }}
			</div>
		</div>
	</div>
</template>
